import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Box,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Autorenew, Event } from "@mui/icons-material"; // Icônes ajoutées
import { useNavigate } from "react-router-dom";
import CheckoutForm from "./CheckoutForm";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useLang } from "../context/LangContext";
import locales from "../locales.json"; // Importation du fichier locales.json
import { useLocation } from "react-router-dom";

const stripePromise = loadStripe(
  "pk_live_51OZsbaJTJI0SN6JVzmviwvsG2w15tcRo0VuNzUY6GevKq7Mz2d7JTRHVtUXCngFwi2GsmTfxw5sgiReQSP5FPwoK0013gFBAbr",
);
//const stripePromise = loadStripe('pk_test_51OZsbaJTJI0SN6JVMuaGDgymdvmnJLSHFXQvIbSa5VD2tlVJxffqLDMiyU9kwnHuBwbaZyaSRllsSropAJ5YLEAy00gUk8u5B6');

/** Full-bleed wrapper for edge-to-edge styling */
function FullBleed({ children, sx }) {
  return (
    <Box
      sx={{
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        width: "100vw",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const PaymentPage = () => {
  const navigate = useNavigate();
  const [billingOpen, setBillingOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentMethod] = useState("card"); // Nouveau state pour le mode de paiement
  const [paymentFrequency, setPaymentFrequency] = useState("annual");
  const [billingInfo, setBillingInfo] = useState({
    firstName: "",
    lastName: "",
    address: "",
    postalCode: "",
    city: "",
  });
  const { lang } = useLang();
  const { payment_page } = locales; // Accéder aux variables de la page de paiement pour la langue sélectionnée

  const location = useLocation();
  const { licenseType } = location.state || { licenseType: "premium" };

  const planDetails = {
    premium: {
      pricePerUser: 53.45,
      pricePerUserAnnual: 588.0,
      minUsers: 1,
      features: [
        payment_page.real_time_transcription[lang],
        payment_page.report_translation[lang],
        payment_page.priority_support[lang],
      ],
    },
    enterprise: {
      pricePerUser: 42.55,
      pricePerUserAnnual: 468.0,
      minUsers: 2,
      features: [
        payment_page.enterprise_feature_1[lang],
        payment_page.enterprise_feature_2[lang],
        payment_page.enterprise_feature_3[lang],
      ],
    },
  };

  const currentPlan = planDetails[licenseType];

  const [userCount, setUserCount] = useState(currentPlan.minUsers);

  // Calcul des montants
  const totalAmount =
    (paymentFrequency === "annual"
      ? currentPlan.pricePerUserAnnual
      : currentPlan.pricePerUser) * userCount; // Montant total, TVA incluse
  const monthlyAmount =
    (paymentFrequency === "annual"
      ? currentPlan.pricePerUserAnnual / 12
      : currentPlan.pricePerUser) * userCount;
  const vatRate = 0.077; // Taux de TVA suisse
  const price = totalAmount / (1 + vatRate); // Prix avant TVA
  const vatAmount = totalAmount - price; // TVA sur le prix

  // Formatage du montant en CHF
  const formatCurrency = (amount) => `${amount.toFixed(2)} CHF`;

  const nextBillingDate = new Date();
  nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
  const formattedNextBillingDate = nextBillingDate.toLocaleDateString(lang, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const nextBillingDateYear = new Date();
  nextBillingDateYear.setFullYear(nextBillingDate.getFullYear() + 1);
  const formattedNextBillingDateYear = nextBillingDateYear.toLocaleDateString(
    lang,
    {
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  );

  const handleBillingSubmit = async () => {
    try {
      const response = await fetchWithAuth(`/subscriptions/save-billing-info`, {
        method: "POST",
        body: JSON.stringify({ billingInfo }),
      });

      const data = await response.json();
      if (data.error) {
        console.error(
          "Erreur lors de l'enregistrement des informations de facturation:",
          data.error,
        );
      } else {
        setBillingOpen(false);
        if (paymentMethod === "card") {
          setPaymentOpen(true);
        } else {
          try {
            const response = await fetchWithAuth(
              `/subscriptions/create-subscription-invoice`,
              {
                method: "POST",
              },
            );

            const data = await response.json();
            if (data.error) {
            } else {
              navigate("/thank-you"); // Redirige vers la page de remerciements
            }
          } catch (err) {}
        }
      }
    } catch (err) {
      console.error("Une erreur est survenue:", err);
    }
  };

  return (
    <Box>
      {/* Hero Section with Payment Content */}
      <FullBleed
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 4, md: 6 },
          marginTop: { xs: -2, md: -2 },
          minHeight: "calc(80vh - 64px)",
          display: "flex",
          alignItems: "center",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          background:
            "linear-gradient(135deg, rgba(59,130,246,.16), rgba(168,85,247,.16))",

          // LEFT photo
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-left.jpg')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "-60px center",
            backgroundSize: { xs: "0 0", md: "auto 100%" },
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },

          // RIGHT photo
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-right.webp')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right center",
            backgroundSize: { xs: "0 0", md: "auto 100%" },
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },
        }}
      >
        {/* Overlay SCRIM */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background:
              "linear-gradient(90deg,rgba(143, 143, 143, 0.01) 0%, #dadef8ff 50%, rgba(150, 150, 150, 0.01) 100%)",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundPosition: "center, center",
            backgroundSize: "100% 100%, 100% 100%",
            mixBlendMode: "normal",
          }}
        />

        {/* Payment Card Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, md: 4 },
          }}
        >
          <Card
            sx={{
              width: "100%",
              maxWidth: 800,
              padding: { xs: 2, sm: 3, md: 4 },
              boxShadow: 3,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
            }}
          >
            <CardContent>
              <Typography
                variant="h5"
                component="div"
                gutterBottom
                sx={{ textAlign: { xs: "center", md: "left" } }}
              >
                {licenseType === "premium"
                  ? payment_page.premium_subscription[lang]
                  : payment_page.enterprise_subscription[lang]}
              </Typography>

              <Grid container spacing={4}>
                {/* Colonne gauche : Informations produit */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ textAlign: { xs: "center", md: "left" } }}
                  >
                    {formatCurrency(monthlyAmount)} / {payment_page.month[lang]}
                  </Typography>
                  <Typography variant="body1" sx={{ marginTop: 2 }}>
                    <ul
                      style={{
                        listStyleType: "none",
                        padding: 0,
                        textAlign: "left",
                      }}
                    >
                      {currentPlan.features.map((feature, index) => (
                        <li key={index}>✅ {feature}</li>
                      ))}
                    </ul>
                  </Typography>
                  {licenseType === "enterprise" && (
                    <TextField
                      type="number"
                      label={payment_page.number_of_users[lang]}
                      value={userCount}
                      onChange={(e) =>
                        setUserCount(
                          Math.max(
                            currentPlan.minUsers,
                            parseInt(e.target.value) || 0,
                          ),
                        )
                      }
                      InputProps={{ inputProps: { min: currentPlan.minUsers } }}
                      fullWidth
                      margin="dense"
                      sx={{ marginTop: 2 }}
                    />
                  )}
                </Grid>

                {/* Colonne droite : Récapitulatif des prix */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {payment_page.sub_total[lang]}
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(price)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 1,
                    }}
                  >
                    <Typography variant="body1">TVA (7,7%)</Typography>
                    <Typography variant="h6">
                      {formatCurrency(vatAmount)}
                    </Typography>
                  </Box>

                  <Divider sx={{ marginY: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 1,
                    }}
                  >
                    <Typography variant="body1">
                      {payment_page.due_today[lang]}
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(totalAmount)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ marginTop: 2 }}
                  >
                    {`${formatCurrency(totalAmount)} ${payment_page.vat_included[lang]}`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {paymentFrequency === "annual"
                      ? `${payment_page.annual_from[lang]} ${formattedNextBillingDateYear}.`
                      : `${payment_page.monthly_from[lang]} ${formattedNextBillingDate}.`}
                  </Typography>
                </Grid>
              </Grid>

              {/* Dropdown pour choisir le moyen de paiement */}
              {/*<FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel id="payment-method-label">{payment_page.payment_method[lang]}</InputLabel>
            <Select
              labelId="payment-method-label"
              id="payment-method"
              value={paymentMethod}
              label={payment_page.payment_method[lang]}
              onChange={(e) => setPaymentMethod(e.target.value)}
              renderValue={(selected) => {
                if (selected === 'card') {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCard sx={{ marginRight: 1 }} />
                      {payment_page.credit_card[lang]}
                    </Box>
                  );
                } else if (selected === 'invoice') {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Receipt sx={{ marginRight: 1 }} />
                      {payment_page.invoice[lang]}
                    </Box>
                  );
                }
                return '';
              }}
            >
              <MenuItem value="card">
                <ListItemIcon>
                  <CreditCard />
                </ListItemIcon>
                <ListItemText primary={payment_page.credit_card[lang]} />
              </MenuItem>
              <MenuItem value="invoice">
                <ListItemIcon>
                  <Receipt />
                </ListItemIcon>
                <ListItemText primary={payment_page.invoice[lang]} />
              </MenuItem>
            </Select>
          </FormControl>*/}

              {/* Dropdown pour choisir la frequence de paiement */}
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <InputLabel id="payment-frequency-label">
                  {payment_page.payment_frequency[lang]}
                </InputLabel>
                <Select
                  labelId="payment-frequency-label"
                  id="payment-frequency"
                  value={paymentFrequency}
                  label={payment_page.payment_frequency[lang]}
                  onChange={(e) => setPaymentFrequency(e.target.value)}
                  renderValue={(selected) => {
                    if (selected === "annual") {
                      return (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Event sx={{ marginRight: 1 }} />
                          {payment_page.annual[lang]}{" "}
                          <span style={{ marginLeft: "8px" }}></span>{" "}
                          <strong>{payment_page.one_month_free[lang]} </strong>
                        </Box>
                      );
                    } else if (selected === "monthly") {
                      return (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Autorenew sx={{ marginRight: 1 }} />
                          {payment_page.monthly[lang]}
                        </Box>
                      );
                    }
                    return "";
                  }}
                >
                  <MenuItem value="annual">
                    <ListItemIcon>
                      <Event />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <>
                          {payment_page.annual[lang]}{" "}
                          <span style={{ marginLeft: "8px" }}></span>
                          <strong>{payment_page.one_month_free[lang]}</strong>
                        </>
                      }
                    />
                  </MenuItem>
                  <MenuItem value="monthly">
                    <ListItemIcon>
                      <Autorenew />
                    </ListItemIcon>
                    <ListItemText primary={payment_page.monthly[lang]} />
                  </MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  marginTop: 2,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  borderRadius: "24px",
                  py: { xs: 1, md: 1.5 },
                }}
                onClick={() => setBillingOpen(true)}
              >
                {payment_page.continue_to_payment[lang]}
              </Button>
            </CardContent>

            <Dialog open={billingOpen} onClose={() => setBillingOpen(false)}>
              <DialogTitle>{payment_page.billing_info[lang]}</DialogTitle>
              <DialogContent>
                <TextField
                  label={payment_page.first_name[lang]}
                  fullWidth
                  margin="dense"
                  value={billingInfo.firstName}
                  onChange={(e) =>
                    setBillingInfo({
                      ...billingInfo,
                      firstName: e.target.value,
                    })
                  }
                  required
                />
                <TextField
                  label={payment_page.last_name[lang]}
                  fullWidth
                  margin="dense"
                  value={billingInfo.lastName}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, lastName: e.target.value })
                  }
                  required
                />
                <TextField
                  label={payment_page.address[lang]}
                  fullWidth
                  margin="dense"
                  value={billingInfo.address}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, address: e.target.value })
                  }
                  required
                />
                <TextField
                  label={payment_page.zip_code[lang]}
                  fullWidth
                  margin="dense"
                  value={billingInfo.postalCode}
                  onChange={(e) =>
                    setBillingInfo({
                      ...billingInfo,
                      postalCode: e.target.value,
                    })
                  }
                  required
                />
                <TextField
                  label={payment_page.city[lang]}
                  fullWidth
                  margin="dense"
                  value={billingInfo.city}
                  onChange={(e) =>
                    setBillingInfo({ ...billingInfo, city: e.target.value })
                  }
                  required
                />

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginTop: 2 }}
                >
                  * {payment_page.required_fields[lang]}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setBillingOpen(false)}>
                  {payment_page.cancel[lang]}
                </Button>
                <Button
                  onClick={handleBillingSubmit}
                  variant="contained"
                  color="primary"
                  disabled={
                    !billingInfo.firstName ||
                    !billingInfo.lastName ||
                    !billingInfo.address ||
                    !billingInfo.postalCode ||
                    !billingInfo.city
                  }
                >
                  {paymentMethod === "card"
                    ? payment_page.continue[lang]
                    : payment_page.confirm_order[lang]}
                </Button>
              </DialogActions>
            </Dialog>

            {paymentMethod === "card" && (
              <Elements stripe={stripePromise}>
                <Dialog
                  open={paymentOpen}
                  onClose={() => setPaymentOpen(false)}
                  maxWidth="xs"
                  fullWidth
                >
                  <DialogTitle>{payment_page.title[lang]}</DialogTitle>
                  <DialogContent>
                    <CheckoutForm
                      onClose={() => setPaymentOpen(false)}
                      frequency={paymentFrequency}
                      licenseType={licenseType}
                      userCount={userCount}
                    />
                  </DialogContent>
                </Dialog>
              </Elements>
            )}
          </Card>
        </Box>
      </FullBleed>
    </Box>
  );
};

export default PaymentPage;
